import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { getApiErrorMessage, isConflictError } from "@/lib/api/api";
import {
  useCreateMenuCategory,
  useDeleteMenuCategory,
  useMenuCategories,
  useRenameMenuCategory,
  useReorderMenuCategories,
} from "@/hooks/menu-categories";
import type { MenuCategory } from "@/types/menu-category";
import { AddCategoryDialog } from "./AddCategoryDialog";
import { CategoryChips } from "./CategoryChips";
import { ConfigureCategoriesDialog } from "./ConfigureCategoriesDialog";
import { DeleteCategoryDialog } from "./DeleteCategoryDialog";

interface MenuCategoryBarProps {
  /** The concept whose categories are shown. */
  companyId: number;
  /** The category the list is filtered to; undefined is "All". */
  activeId?: number;
  onChange: (id: number | undefined) => void;
  /**
   * Whether the viewer may build menus (`menu:write`). Without it the bar only
   * filters: Add and Configure are not offered, and the API refuses the writes
   * regardless.
   */
  canWrite: boolean;
}

/**
 * The category chip bar over the Menu Items list, with the dialogs that add,
 * rename, reorder and delete categories. It owns those dialogs so the list
 * screen only has to say which concept it is showing.
 */
export function MenuCategoryBar({ companyId, activeId, onChange, canWrite }: MenuCategoryBarProps) {
  const { t } = useLingui();
  const { data: categories = [], isSuccess, isPlaceholderData } = useMenuCategories(companyId);

  const createCategory = useCreateMenuCategory();
  const renameCategory = useRenameMenuCategory();
  const reorderCategories = useReorderMenuCategories();
  const deleteCategory = useDeleteMenuCategory();

  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [deleting, setDeleting] = useState<MenuCategory | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // A category in the URL that this concept does not have (a link from another
  // concept, or one deleted since) would filter to nothing with no chip lit.
  // Only judged against this concept's own list, not the previous one kept
  // on screen while it loads.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  useEffect(() => {
    if (!isSuccess || isPlaceholderData || activeId === undefined) return;
    if (!categories.some((c) => c.id === activeId)) onChangeRef.current(undefined);
  }, [isSuccess, isPlaceholderData, activeId, categories]);

  if (!canWrite && categories.length === 0) return null;

  const nameTaken = (name: string) => t`A category named "${name}" already exists in this concept`;

  return (
    <>
      <CategoryChips
        categories={categories}
        activeId={activeId}
        onChange={onChange}
        onAddCategory={
          canWrite
            ? () => {
                setAddError(null);
                setAddOpen(true);
              }
            : undefined
        }
        onConfigureCategories={canWrite ? () => setConfigureOpen(true) : undefined}
      />

      {canWrite && (
        <>
          <AddCategoryDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            isPending={createCategory.isPending}
            error={addError}
            onSave={(name) =>
              createCategory.mutate(
                { companyId, name },
                {
                  onSuccess: () => {
                    setAddOpen(false);
                    toast.success(t`Category added`);
                  },
                  onError: (error) =>
                    setAddError(
                      isConflictError(error)
                        ? nameTaken(name)
                        : getApiErrorMessage(error, t`Failed to add the category`),
                    ),
                },
              )
            }
          />

          <ConfigureCategoriesDialog
            open={configureOpen}
            onOpenChange={setConfigureOpen}
            categories={categories}
            onAddCategory={() => {
              setConfigureOpen(false);
              setAddError(null);
              setAddOpen(true);
            }}
            onRename={(id, name) =>
              renameCategory.mutateAsync({ id, name }).catch((error: unknown) => {
                throw new Error(
                  isConflictError(error)
                    ? nameTaken(name)
                    : getApiErrorMessage(error, t`Failed to rename the category`),
                );
              })
            }
            onReorder={(orderedIds) =>
              reorderCategories.mutate(
                { companyId, orderedIds },
                { onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to save the order`)) },
              )
            }
            onDelete={(category) => {
              setDeleteError(null);
              setDeleting(category);
            }}
          />

          <DeleteCategoryDialog
            category={deleting}
            others={categories}
            isPending={deleteCategory.isPending}
            error={deleteError}
            onOpenChange={(open) => {
              if (!open) setDeleting(null);
            }}
            onConfirm={(category, reassignToId) =>
              deleteCategory.mutate(
                { id: category.id, reassignToId },
                {
                  onSuccess: () => {
                    setDeleting(null);
                    if (activeId === category.id) onChange(undefined);
                    toast.success(t`Category deleted`);
                  },
                  onError: (error) =>
                    setDeleteError(getApiErrorMessage(error, t`Failed to delete the category`)),
                },
              )
            }
          />
        </>
      )}
    </>
  );
}
